import { FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { pageUrl } from "../../../../utils/urls";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { CheckIcon, DotMenuVerticalIcon, UndoIcon } from "mds/icons";
import { storeApi } from "store/index";

type THProps = {
  className?: string;
  children: ReactNode;
  isVertical?: boolean;
  verticalDivClassName?: string;
  verticalDivTitle?: string;
};
export const TH: FC<THProps> = ({
  className,
  isVertical,
  verticalDivClassName,
  verticalDivTitle,
  children,
}) => (
  <th
    className={twMerge(
      "sticky top-0 py-2 pr-4 text-left font-medium text-black-tint-20",
      isVertical ? "w-8 content-end" : "whitespace-nowrap",
      className,
    )}
  >
    {isVertical ? (
      <div
        className={twMerge(
          "max-h-32 max-w-[80px] rotate-180 content-end overflow-hidden text-ellipsis whitespace-nowrap pb-2 pl-2 pt-2 text-left [writing-mode:vertical-lr]",
          verticalDivClassName,
        )}
        title={verticalDivTitle}
      >
        {children}
      </div>
    ) : (
      children
    )}
  </th>
);

type TDProps = { className?: string; title?: string; children: ReactNode };
export const TD: FC<TDProps> = ({ className, title, children }) => (
  <td className={twMerge("whitespace-nowrap pr-4", className)} title={title}>
    {children}
  </td>
);

type THeadProps = { className?: string; children: ReactNode };
export const THead: FC<THeadProps> = ({ className, children }) => (
  <thead className={twMerge("border-b border-black-tint-70", className)}>{children}</thead>
);

type TableProps = { className?: string; children: ReactNode };
export const Table: FC<TableProps> = ({ className, children }) => (
  <table className={twMerge("w-full whitespace-nowrap text-xs", className)}>{children}</table>
);

type TRProps = { className?: string; children: ReactNode };
export const TR: FC<TRProps> = ({ className, children }) => (
  <tr className={twMerge("border-b border-black-tint-90", className)}>{children}</tr>
);

interface MarkAssessmentCompleteMenuProps {
  isMarkedComplete: boolean;
  isCourseAssessment?: boolean;
  pageId: string;
  pageTitle: string;
  courseId: string;
  onChange: () => void;
}
export const MarkAssessmentCompleteMenu: FC<MarkAssessmentCompleteMenuProps> = ({
  isMarkedComplete,
  isCourseAssessment,
  pageId,
  pageTitle,
  courseId,
  onChange,
}) => {
  return (
    <Menu
      align="end"
      menuButton={
        <Button kind="transparentSquareLegacy" size="xs">
          <DotMenuVerticalIcon />
        </Button>
      }
    >
      {isMarkedComplete ? (
        <MenuItem
          eventName={`${isCourseAssessment ? "Course" : "Home"} - Assessments / Undo mark as complete`}
          eventProps={{
            pageTitle,
            pageUrl: pageUrl(courseId, pageId),
            pageId,
          }}
          onClick={async () => {
            await storeApi.pages.partial_update({
              id: pageId,
              assessing_marked_complete_at: null,
            });
            onChange();
          }}
        >
          <IconText
            iconStart={<UndoIcon />}
            text={t("sidebar_options_menu.mark_as_complete_undo")}
          />
        </MenuItem>
      ) : (
        <MenuItem
          eventName={`${isCourseAssessment ? "Course" : "Home"} - Assessments / Mark as complete`}
          eventProps={{
            pageTitle,
            pageUrl: pageUrl(courseId, pageId),
            pageId,
          }}
          onClick={async () => {
            await storeApi.pages.partial_update({
              id: pageId,
              assessing_marked_complete_at: new Date().toISOString(),
            });
            onChange();
          }}
        >
          <IconText iconStart={<CheckIcon />} text={t("sidebar_options_menu.mark_as_complete")} />
        </MenuItem>
      )}
    </Menu>
  );
};
